// Unified Context Bar - Adapts to current view mode
import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { getSignedPictureUrl } from '../../modules/dataService.js'
import {
    appViewMode
} from '../../state/appViewMode.js'
import { currentStep } from '../../state/benchmarkingWorkflowState.js'
import {
    basis,
    COMPARE_SECTIONS,
    activeCompareSection,
    activeCompareView,
    activeElectricalSubSection,
    activeSection,
    availableCompareViews,
    displayMode,
    freePlotColorBy,
    freePlotSizeBy,
    layoutMode,
    myPlotColorBy,
    myPlotSizeBy,
    scorePlotColorBy,
    scorePlotSizeBy,
    showColumnPicker,
    showGridBadges,
    showGridImages,
    sortBy,
    sortOrder,
    tableAlternateRowColors,
    tableDensity,
    tableHoverHighlight,
    tableRowHeight,
    tableShowColumnBorders,
    tableShowColumnMenu,
    tableShowRowNumbers,
    tableSortBy,
    tableSortOrder,
    tableVisibleColumns
} from '../../state/globalSignals.js'
import {
    IconArrowLeft,
    IconChart,
    IconCheck,
    IconGrid,
    IconLayers,
    IconMap,
    IconPlus,
    IconSettings,
    IconTable
} from '../shared/Icons.jsx'
import {
    ADDED_DATE_FALLBACK_SORT,
    addedDateSortAvailable,
    addedDateSortDowngraded
} from '../../state/filterStore.js'
import { RADAR_SECTIONS, SECTION_BY_ID } from '../radar/radarConfig.js'
import { radarActivePlotIndex, radarActivePlotCount, radarExportAction } from '../../state/radarState.js'
import { getAdminRole } from '../../state/userState.js'
import { findCellBySlug } from '../../utils/cellSlug.js'

/**
 * The Explorer's sort keys, as `[value, label]`.
 *
 * ONE list feeding BOTH sort dropdowns (GridViewControls writes `sortBy`,
 * TableViewControls writes `tableSortBy` — two independent signals by design, so
 * a user can hand-sort each view differently). The two option lists used to be
 * separate copies of the same six entries; keeping them separate while adding
 * entries would have meant a sort that exists in the grid and not in the table.
 *
 * Mirrors `SORT_KEYS` in `backend/app/api/v1/chat/explorer_vocab.py`, which is
 * what Ion may name in `set_view`. The drift guard is
 * `frontend/tests/explorer-commands/sort-vocab-in-sync.test.js` — equality, not
 * a subset: a key the UI offers and Ion cannot name is a needless asymmetry, and
 * a key Ion names that no view implements silently sorts by nothing (both
 * comparators `return 0` in their `default` branch).
 *
 * `aging` and `batchVariation` are the two quality metrics. They land in the UI
 * rather than behind an Ion-only path because the data is already in the
 * explorer payload and both are already table columns — the dropdown was simply
 * missing them. Cells with no value sort last in both directions; see
 * `utils/cellSort.js`.
 */
export const SORT_OPTIONS = [
  ['name', 'Name'],
  ['manufacturer', 'Manufacturer'],
  ['energy', 'Energy'],
  ['power', 'Power'],
  ['capacity', 'Capacity'],
  ['year', 'Release year'],
  ['aging', 'Post-Formation Loss'],
  ['batchVariation', 'Cell-to-Cell Deviation'],
  // Rendered by <AddedDateSortOption /> (conditional — see the dropdowns below),
  // so this label is the fallback for anything that reads the list rather than
  // the DOM. Kept identical to the component's own text.
  ['addedDate', 'Added to catalogue'],
]

export default function UnifiedContextBar() {
  const mode = appViewMode.value
  const layout = layoutMode.value

  // Track URL search params to trigger re-renders on URL changes
  const [searchParams, setSearchParams] = useState(() =>
    typeof window !== 'undefined' ? window.location.search : ''
  )

  // Poll for URL changes
  useEffect(() => {
    const checkUrl = () => {
      const newSearch = window.location.search
      if (newSearch !== searchParams) {
        console.log('[UnifiedContextBar] URL search changed:', searchParams, '->', newSearch)
        setSearchParams(newSearch)
      }
    }
    const interval = setInterval(checkUrl, 50)
    return () => clearInterval(interval)
  }, [searchParams])

  // Check for cell query param to determine single-cell mode
  const url = typeof window !== 'undefined' ? new URL(window.location) : null
  const cellParam = url?.searchParams.get('cell')

  // Calculate effective view mode
  const viewMode = cellParam ? 'single-cell' : (layout === 'compare' ? 'compare' : mode)

  console.log('[UnifiedContextBar] Render:', { mode, layout, cellParam, viewMode, searchParams })

  return (
    <div className="unified-context-bar" data-view-mode={viewMode}>
      <div className="context-bar-inner">
        {/* Single Cell Mode - Check query param first */}
        {cellParam ? (
          <CellsSingleContextBar key={cellParam} />
        ) : layout === 'compare' ? (
          /* Compare Mode - Check layoutMode */
          <CompareContextBar />
        ) : mode.startsWith('radar-') ? (
          /* Radar Mode */
          <RadarContextBar />
        ) : mode.startsWith('benchmarking-') ? (
          /* Benchmarking Mode */
          <BenchmarkingContextBar />
        ) : mode.startsWith('cells-') ? (
          /* Cells Grid/Ragone/Table Mode */
          <CellsGridContextBar />
        ) : null}
      </div>
    </div>
  )
}

/**
 * Context Bar for Compare Mode
 * Back button (left) + Section tabs + View toggle & controls (right)
 */
function CompareContextBar() {
  const [, setLocation] = useLocation()
  const activeSection = activeCompareSection.value
  const activeView = activeCompareView.value
  const availableViews = availableCompareViews.value

  const handleSectionChange = (sectionId) => {
    activeCompareSection.value = sectionId
    // Reset to first available view for this section
    const section = COMPARE_SECTIONS.find(s => s.id === sectionId)
    if (section && section.views.length > 0) {
      activeCompareView.value = section.views[0]
    }
  }

  const handleViewChange = (view) => {
    activeCompareView.value = view
  }

  const handleBack = () => {
    // Navigate back to Cells explorer
    setLocation('/explorer?view=grid')
  }

  return (
    <>
      {/* Left: Back Button + Section Tabs */}
      <div className="compare-left-controls">
        <button
          className="back-button"
          onClick={handleBack}
          title="Back to Cells"
        >
          <IconArrowLeft size={20} />
        </button>

        <div className="section-tabs" data-tour="compare-section-tabs">
          {COMPARE_SECTIONS.map(section => (
            <button
              key={section.id}
              className={`section-tab ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => handleSectionChange(section.id)}
              title={section.title}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>

      {/* Right: Context Controls (View Toggle + Export) */}
      <div className="context-controls">
        {/* View Toggle (if section has multiple views) */}
        {availableViews.length > 1 && (
          <div className="context-control-group">
            <span className="context-control-label">View:</span>
            <div className="view-toggle-mini" data-tour="compare-view-toggle">
              {availableViews.includes('table') && (
                <button
                  className={`view-toggle-btn ${activeView === 'table' ? 'active' : ''}`}
                  onClick={() => handleViewChange('table')}
                  title="Table View"
                >
                  <IconTable size={14} />
                </button>
              )}
              {availableViews.includes('charts') && (
                <button
                  className={`view-toggle-btn ${activeView === 'charts' ? 'active' : ''}`}
                  onClick={() => handleViewChange('charts')}
                  title="Charts View"
                >
                  <IconChart size={14} />
                </button>
              )}
              {availableViews.includes('2d') && (
                <button
                  className={`view-toggle-btn ${activeView === '2d' ? 'active' : ''}`}
                  onClick={() => handleViewChange('2d')}
                >
                  {activeSection === 'electrical' && activeElectricalSubSection.value === 'voltage'
                    ? '2D Curve'
                    : activeSection === 'electrical' && activeElectricalSubSection.value === 'ac-impedance'
                      ? 'Nyquist'
                      : '2D Curves'}
                </button>
              )}
              {availableViews.includes('3d') && (
                (activeSection === 'electrical' && activeElectricalSubSection.value !== 'voltage') ||
                activeSection === 'thermal'
              ) && (
                <button
                  className={`view-toggle-btn ${activeView === '3d' ? 'active' : ''}`}
                  onClick={() => handleViewChange('3d')}
                >
                  {activeSection === 'electrical' && activeElectricalSubSection.value === 'ac-impedance'
                    ? 'Bode'
                    : '3D Surfaces'}
                </button>
              )}
              {availableViews.includes('3d-model') && (
                <button
                  className={`view-toggle-btn ${activeView === '3d-model' ? 'active' : ''}`}
                  onClick={() => handleViewChange('3d-model')}
                  title="3D Model"
                >
                  <IconLayers size={14} />
                </button>
              )}
              {availableViews.includes('graph') && (
                <button
                  className={`view-toggle-btn ${activeView === 'graph' ? 'active' : ''}`}
                  onClick={() => handleViewChange('graph')}
                  title="Graph"
                >
                  <IconMap size={14} />
                </button>
              )}
              {availableViews.includes('surface') && (
                <button
                  className={`view-toggle-btn ${activeView === 'surface' ? 'active' : ''}`}
                  onClick={() => handleViewChange('surface')}
                  title="Surface Plot"
                >
                  <IconLayers size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/**
 * Context Bar for Cells Grid/Graph/Table modes
 */
function CellsGridContextBar() {
  const mode = appViewMode.value
  const [, setLocation] = useLocation()

  const handleViewChange = (view) => {
    setLocation(`/explorer?view=${view}`)
  }

  return (
    <>
      {/* Left: View Switcher */}
      <div className="view-switcher" data-tour="explorer-view-switcher">
        <button
          className={`view-tab ${mode === 'cells-grid' ? 'active' : ''}`}
          onClick={() => handleViewChange('grid')}
          title="Grid View"
        >
          <IconGrid size={18} />
          <span>Grid</span>
        </button>

        <button
          className={`view-tab ${mode === 'cells-table' ? 'active' : ''}`}
          onClick={() => handleViewChange('table')}
          title="Table View"
        >
          <IconTable size={18} />
          <span>Table</span>
        </button>

        <button
          className={`view-tab ${mode === 'cells-graph' ? 'active' : ''}`}
          onClick={() => handleViewChange('graph')}
          title="Graph - Customizable Chart"
        >
          <IconChart size={18} />
          <span>Graph</span>
        </button>
      </div>

      {/* Right: View-specific Controls */}
      <div className="context-controls">
        {mode === 'cells-grid' && <GridViewControls />}
        {mode === 'cells-graph' && <FreePlotViewControls />}
        {mode === 'cells-table' && <TableViewControls />}
      </div>
    </>
  )
}

/**
 * Graph Size By Select with all KPI options
 */
function FreePlotSizeBySelect() {
  // Import AVAILABLE_KPIS from FreePlotECharts
  // We'll build this dynamically to match the same structure as in FreePlotSettingsOverlay

  const SIZE_OPTIONS_GROUPED = {
    'Fixed': [{ value: 'none', label: 'Fixed Size' }],
    'Performance': [
      { value: 'capacity_c10_abs', label: 'Capacity C10 (Ah)' },
      { value: 'energy_c10_grav', label: 'Energy C10 (Wh/kg)' },
      { value: 'energy_c10_vol', label: 'Energy C10 (Wh/L)' },
      { value: 'power_peak_grav', label: 'Peak Power (W/kg)' },
      { value: 'power_peak_vol', label: 'Peak Power (W/L)' },
      { value: 'current_peak_abs', label: 'Peak Current (A)' },
      { value: 'power_cont_grav', label: 'Cont. Power (W/kg)' },
      { value: 'current_cont_abs', label: 'Cont. Current (A)' }
    ],
    'Standard': [
      { value: 'eta_dc_25', label: 'DC Efficiency @ 25°C (%)' },
      { value: 'P_dc_25_grav', label: 'DC Power @ 25°C (W/kg)' },
      { value: 'W_dc_25_abs', label: 'DC Energy @ 25°C (Wh)' },
      { value: 'R_dc_25', label: 'DCIR @ 25°C (mΩ)' }
    ],
    'Geometry': [
      { value: 'mass', label: 'Cell Mass (g)' },
      { value: 'surface_area', label: 'Surface Area (cm²)' },
      { value: 'volume', label: 'Cell Volume (cm³)' },
      { value: 'voltage', label: 'Mean Voltage (V)' }
    ],
    'Post-Formation Loss': [
      { value: 'aging_cap_loss', label: 'Capacity Loss (%)' },
      { value: 'aging_eis_increase', label: 'EIS Increase (%)' }
    ],
    'Cell-to-Cell Variation': [
      { value: 'batch_cap_variation', label: 'Capacity Variation (%)' },
      { value: 'batch_eis_variation', label: 'EIS Variation (%)' }
    ],
    'Date': [
      { value: 'year', label: 'Year' }
    ]
  }

  return (
    <select
      className="context-control-select"
      value={freePlotSizeBy.value}
      onChange={(e) => freePlotSizeBy.value = e.target.value}
    >
      {Object.entries(SIZE_OPTIONS_GROUPED).map(([group, options]) => (
        <optgroup label={group}>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  )
}

/**
 * The "Added to catalogue" sort option — rendered ONLY when the loaded catalogue
 * actually carries insertion dates.
 *
 * The Explorer has two load paths and only the primary one (/explorer/v2)
 * carries `created_at`. On the legacy fallback path the comparator would be a
 * total tie: the user picks "Added to catalogue", nothing reorders, and nothing
 * explains why. So the option is not offered there at all — see
 * `state/filterStore.js` → addedDateSortAvailable.
 */
export function AddedDateSortOption() {
  if (!addedDateSortAvailable.value) return null
  return <option value="addedDate">Added to catalogue</option>
}

/**
 * Sort keys Ion can set but the dropdown does not advertise (BA, 2026-08-03):
 * "sollen nicht dauerhaft bei sort stehen, sondern dynamisch eingefügt werden,
 * wenn der Nutzer Ion nach der Sortierung fragt".
 *
 * These three shipped with D1 as permanent entries and made the dropdown longer
 * than the sorts most people ever want — the two quality metrics in particular
 * are specialist questions, and "Added to catalogue" sits confusingly next to
 * "Release year" for anyone not already thinking about catalogue insertion.
 *
 * They remain fully supported keys — SORT_OPTIONS still lists all of them, which
 * is what keeps the set checkable against the backend vocabulary
 * (tests/explorer-commands/sort-vocab-in-sync.test.js) and what the comparator
 * and Ion's `set_view` continue to use. Only the OFFER is withheld.
 */
const ION_ONLY_SORT_KEYS = new Set(['aging', 'batchVariation', 'addedDate'])

/**
 * The `<option>` list for a sort dropdown, given the sort that is currently
 * active in it.
 *
 * An Ion-only key appears exactly while it IS the active sort. That is not
 * cosmetic: a `<select>` whose `value` matches no `<option>` renders blank in
 * every browser, so hiding an active key would leave the control claiming
 * nothing is selected while the grid is visibly reordered — and the user could
 * not switch back off it. Showing it keeps Ion's change visible and reversible,
 * and it disappears again the moment they pick something else.
 *
 * Shared by both dropdowns (grid and table) so the rule cannot drift between
 * them; each passes its own active key, since the two sort independently.
 *
 * @param {{activeSort: string}} props
 */
export function SortOptionList({ activeSort }) {
  return SORT_OPTIONS.map(([value, label]) => {
    if (ION_ONLY_SORT_KEYS.has(value) && value !== activeSort) return null
    // `addedDate` keeps its own availability guard on top of the rule above: on
    // the legacy load path the catalogue carries no insertion dates, so the
    // comparator would be a total tie. Ion setting it does not change that.
    if (value === 'addedDate') return <AddedDateSortOption key={value} />
    return (
      <option key={value} value={value}>
        {label}
      </option>
    )
  })
}

/**
 * The visible half of the same guard: says out loud that a REQUESTED
 * catalogue-date sort (deep link `?sort=addedDate`, or a remembered choice) had
 * to be dropped. Without this the downgrade would be as silent as the bug.
 */
export function AddedDateSortNotice() {
  if (!addedDateSortDowngraded.value || addedDateSortAvailable.value) return null
  return (
    <span
      className="context-control-note"
      data-testid="added-date-sort-notice"
      // Inline so the notice is legible without a new CSS rule (this file's
      // stylesheet is not part of the change): muted, small, never clipped.
      style={{ fontSize: '0.75rem', opacity: 0.8, whiteSpace: 'nowrap' }}
      title={
        'The catalogue-insertion date is not part of this data load, so cells cannot be ' +
        'ordered by it. Reload the Explorer to try again.'
      }
    >
      “Added to catalogue” unavailable — sorted by {ADDED_DATE_FALLBACK_SORT}
    </span>
  )
}

/**
 * Grid View Controls
 */
function GridViewControls() {
  return (
    <div className="context-control-group" data-tour="explorer-sort-controls">
      {/* Sort */}
      <div className="context-control">
        <label className="context-control-label">Sort</label>
        <select
          className="context-control-select"
          value={sortBy.value}
          onChange={(e) => sortBy.value = e.target.value}
        >
          {/* ONE list (SORT_OPTIONS) feeding BOTH sort dropdowns through ONE
              renderer, so the grid's and the table's can never offer different
              keys — and so the set stays checkable against the backend
              vocabulary (tests/explorer-commands/sort-vocab-in-sync.test.js).
              Two labels that cannot be mistaken for one another: 'Release year'
              is when the MANUFACTURER released the cell, 'Added to catalogue' is
              when BATEMO added it. */}
          <SortOptionList activeSort={sortBy.value} />
        </select>
        <button
          className="context-control-btn context-control-btn--icon"
          onClick={() => sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'}
          title={sortOrder.value === 'asc' ? 'Ascending order' : 'Descending order'}
        >
          {sortOrder.value === 'asc' ? '↑' : '↓'}
        </button>
        <AddedDateSortNotice />
      </div>

      {/* Display Mode */}
      <div className="context-control">
        <label className="context-control-label">Display</label>
        <select
          className="context-control-select"
          value={displayMode.value}
          onChange={(e) => displayMode.value = e.target.value}
        >
          <option value="compact">Compact</option>
          <option value="comfortable">Comfortable</option>
        </select>
      </div>

      {/* Grid Settings Dropdown */}
      <div className="context-control context-control--menu">
        <button className="context-control-btn" title="Grid Settings">
          <IconGrid size={16} />
        </button>
        <div className="context-dropdown">
          <label className="context-dropdown-item">
            <input
              type="checkbox"
              checked={showGridImages.value}
              onChange={(e) => showGridImages.value = e.target.checked}
            />
            <span>Show Images</span>
          </label>
          <label className="context-dropdown-item">
            <input
              type="checkbox"
              checked={showGridBadges.value}
              onChange={(e) => showGridBadges.value = e.target.checked}
            />
            <span>Show Badges</span>
          </label>
        </div>
      </div>
    </div>
  )
}

/**
 * Graph View Controls (cells-graph mode)
 */
function FreePlotViewControls() {
  return (
    <div className="context-control-group" data-tour="graph-color-size">
      {/* Color By */}
      <div className="context-control">
        <label className="context-control-label">Color</label>
        <select
          className="context-control-select"
          value={freePlotColorBy.value}
          onChange={(e) => freePlotColorBy.value = e.target.value}
        >
          <option value="main-chemistry">Main Chemistry</option>
          <option value="cathode-chemistry">Cathode Chemistry</option>
          <option value="anode-chemistry">Anode Chemistry</option>
          <option value="technology">Technology</option>
          <option value="format">Format</option>
          <option value="housing">Housing</option>
          <option value="manufacturer">Manufacturer</option>
          <option value="year">Year</option>
        </select>
      </div>

      {/* Size By - All KPIs grouped by category */}
      <div className="context-control">
        <label className="context-control-label">Size</label>
        <select
          className="context-control-select"
          value={freePlotSizeBy.value}
          onChange={(e) => freePlotSizeBy.value = e.target.value}
        >
          <option value="none">None (Fixed Size)</option>

          <optgroup label="Performance">
            <option value="capacity_c10_abs">Capacity (Ah)</option>
            <option value="capacity_c10_grav">Grav. Capacity (Ah/kg)</option>
            <option value="capacity_c10_vol">Vol. Capacity (Ah/L)</option>
            <option value="energy_c10_abs">Energy (Wh)</option>
            <option value="energy_c10_grav">Grav. Energy (Wh/kg)</option>
            <option value="energy_c10_vol">Vol. Energy (Wh/L)</option>
            <option value="power_peak_abs">Peak Power (W)</option>
            <option value="power_peak_grav">Grav. Peak Power (W/kg)</option>
            <option value="power_peak_vol">Vol. Peak Power (W/L)</option>
            <option value="power_cont_abs">Continuous Power (W)</option>
            <option value="power_cont_grav">Grav. Cont. Power (W/kg)</option>
            <option value="power_cont_vol">Vol. Cont. Power (W/L)</option>
            <option value="current_peak_abs">Peak Current (A)</option>
            <option value="current_cont_abs">Continuous Current (A)</option>
          </optgroup>

          <optgroup label="DC25 Measurements">
            <option value="eta_dc_25">DC Efficiency (%)</option>
            <option value="P_dc_25_abs">DC Power (W)</option>
            <option value="W_dc_25_abs">DC Energy (Wh)</option>
            <option value="I_dc_25_abs">DC Current (A)</option>
            <option value="t_max_dc_25">Max Duration (s)</option>
            <option value="R_dc_25">DCIR (mΩ)</option>
            <option value="R_EIS_25">EIS Resistance (mΩ)</option>
            <option value="delta_T_dc_25">Temperature Rise (K)</option>
            <option value="P_loss_dc_25_abs">Power Loss (W)</option>
          </optgroup>

          <optgroup label="Geometry">
            <option value="mass">Cell Mass (g)</option>
            <option value="surface_area">Surface Area (cm²)</option>
            <option value="volume">Cell Volume (cm³)</option>
            <option value="voltage">Mean Voltage (V)</option>
          </optgroup>

          <optgroup label="Quality">
            <option value="aging_cap_loss">Capacity Loss (%)</option>
            <option value="aging_eis_increase">EIS Increase (%)</option>
            <option value="batch_cap_variation">Capacity Variation (%)</option>
            <option value="batch_eis_variation">EIS Variation (%)</option>
          </optgroup>

          <optgroup label="Date">
            <option value="year">Year</option>
          </optgroup>
        </select>
      </div>
    </div>
  )
}

/**
 * Table View Controls
 */
function TableViewControls() {
  const isCustomMode = tableDensity.value === 'custom'

  const toggleColumn = (columnKey) => {
    tableVisibleColumns.value = {
      ...tableVisibleColumns.value,
      [columnKey]: !tableVisibleColumns.value[columnKey]
    }
  }

  return (
    <div className="context-control-group">
      {/* Sort */}
      <div className="context-control">
        <label className="context-control-label">Sort</label>
        <select
          className="context-control-select"
          value={tableSortBy.value}
          onChange={(e) => tableSortBy.value = e.target.value}
        >
          {/* Same renderer as the grid's dropdown, with the TABLE's own active
              key — the two views sort independently, so an Ion-only key must
              appear in the one it is active in and not in the other. */}
          <SortOptionList activeSort={tableSortBy.value} />
        </select>
        <button
          className="context-control-btn context-control-btn--icon"
          onClick={() => tableSortOrder.value = tableSortOrder.value === 'asc' ? 'desc' : 'asc'}
          title={tableSortOrder.value === 'asc' ? 'Ascending order' : 'Descending order'}
        >
          {tableSortOrder.value === 'asc' ? '↑' : '↓'}
        </button>
        <AddedDateSortNotice />
      </div>

      {/* View Mode (renamed from Density) */}
      <div className="context-control">
        <label className="context-control-label">View</label>
        <select
          className="context-control-select"
          value={tableDensity.value}
          onChange={(e) => tableDensity.value = e.target.value}
        >
          <option value="standard">Standard</option>
          <option value="detailed">Detailed</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      {/* Column Customize Dropdown (Custom Mode Only) */}
      {isCustomMode && (
        <div className="context-control context-control--menu">
          <button
            className="context-control-btn"
            onClick={() => showColumnPicker.value = !showColumnPicker.value}
            title="Customize Columns"
          >
            <IconTable size={16} />
            <span className="btn-label">Columns</span>
          </button>
          {showColumnPicker.value && (
            <div className="context-dropdown">
              <div className="context-dropdown-title">Select Columns</div>
              <div className="context-dropdown-divider"></div>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.name}
                  onChange={() => toggleColumn('name')}
                />
                <span>Name</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.manufacturer}
                  onChange={() => toggleColumn('manufacturer')}
                />
                <span>Manufacturer</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.format}
                  onChange={() => toggleColumn('format')}
                />
                <span>Format</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.nomCapacity}
                  onChange={() => toggleColumn('nomCapacity')}
                />
                <span>Nom. Capacity</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.energyPower}
                  onChange={() => toggleColumn('energyPower')}
                />
                <span>Energy/Power</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.batchVar}
                  onChange={() => toggleColumn('batchVar')}
                />
                <span>Cell-to-Cell Variation</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.aging}
                  onChange={() => toggleColumn('aging')}
                />
                <span>Post-Formation Loss</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.chemistry}
                  onChange={() => toggleColumn('chemistry')}
                />
                <span>Chemistry</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.voltage}
                  onChange={() => toggleColumn('voltage')}
                />
                <span>Voltage</span>
              </label>
              <label className="context-dropdown-item context-dropdown-item--checkbox">
                <input
                  type="checkbox"
                  checked={tableVisibleColumns.value.mass}
                  onChange={() => toggleColumn('mass')}
                />
                <span>Mass</span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Table Settings Dropdown (Sliders icon - always visible) */}
      <div className="context-control context-control--menu">
        <button
          className="context-control-btn"
          onClick={() => tableShowColumnMenu.value = !tableShowColumnMenu.value}
          title="Table Settings"
        >
          <IconSettings size={16} />
        </button>
        {tableShowColumnMenu.value && (
          <div className="context-dropdown">
            <div className="context-dropdown-title">Table Settings</div>
            <div className="context-dropdown-divider"></div>
            <label className="context-dropdown-item context-dropdown-item--checkbox">
              <input
                type="checkbox"
                checked={tableShowRowNumbers.value}
                onChange={() => tableShowRowNumbers.value = !tableShowRowNumbers.value}
              />
              <span>Show Row Numbers</span>
            </label>
            <label className="context-dropdown-item context-dropdown-item--checkbox">
              <input
                type="checkbox"
                checked={tableAlternateRowColors.value}
                onChange={() => tableAlternateRowColors.value = !tableAlternateRowColors.value}
              />
              <span>Alternate Row Colors</span>
            </label>
            <label className="context-dropdown-item context-dropdown-item--checkbox">
              <input
                type="checkbox"
                checked={tableShowColumnBorders.value}
                onChange={() => tableShowColumnBorders.value = !tableShowColumnBorders.value}
              />
              <span>Column Borders</span>
            </label>
            <label className="context-dropdown-item context-dropdown-item--checkbox">
              <input
                type="checkbox"
                checked={tableHoverHighlight.value}
                onChange={() => tableHoverHighlight.value = !tableHoverHighlight.value}
              />
              <span>Hover Highlight</span>
            </label>
            <div className="context-dropdown-divider"></div>
            <div className="context-dropdown-item context-dropdown-item--select-container">
              <span className="context-dropdown-label-small">Row Height</span>
              <select
                className="context-dropdown-select"
                value={tableRowHeight.value}
                onChange={(e) => tableRowHeight.value = e.target.value}
              >
                <option value="compact">Compact</option>
                <option value="normal">Normal</option>
                <option value="spacious">Spacious</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Context Bar for Single Cell Mode
 * Replaces the old DrawerHeader with Back button + Metadata + Section tabs
 */
function CellsSingleContextBar() {
  const [location, setLocation] = useLocation()

  // Get cell slug and section from URL query params
  const url = typeof window !== 'undefined' ? new URL(window.location) : null
  const cellSlug = url?.searchParams.get('cell')

  // Use the global activeSection signal instead of local state
  const section = activeSection.value

  // Load cell data
  const [cellData, setCellData] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [cellId, setCellId] = useState(null)
  const [inCompare, setInCompare] = useState(false)

  useEffect(() => {
    console.log('[CellsSingleContextBar] useEffect triggered with cellSlug:', cellSlug)
    if (!cellSlug) {
      setCellData(null)
      setImageUrl(null)
      setCellId(null)
      return
    }

    // Reset state when cellSlug changes
    setCellData(null)
    setImageUrl(null)
    setCellId(null)

    // Import allCells to find cell by slug
    import('../../state/globalSignals.js').then(({ allCells }) => {
      // Wait for allCells to be populated (check every 100ms, max 5s)
      let attempts = 0
      const maxAttempts = 50

      const findCell = () => {
        attempts++

        if (allCells.value.length === 0) {
          if (attempts < maxAttempts) {
            console.log('[UnifiedContextBar] Waiting for cells to load... attempt', attempts)
            setTimeout(findCell, 100)
            return
          } else {
            console.error('[UnifiedContextBar] Timeout waiting for cells to load')
            setCellData(null)
            setImageUrl(null)
            setCellId(null)
            return
          }
        }

        // Find cell by matching slug (a pre-#18 legacy slug resolves too)
        const cell = findCellBySlug(allCells.value, cellSlug)

        if (!cell) {
          console.error('[UnifiedContextBar] Cell not found for slug:', cellSlug)
          console.error('[UnifiedContextBar] Available cells:', allCells.value.length)
          console.error('[UnifiedContextBar] First cell sample:', allCells.value[0])
          setCellData(null)
          setImageUrl(null)
          setCellId(null)
          return
        }

        console.log('[UnifiedContextBar] Found cell:', cell.manufacturer, cell.name, 'ID:', cell.id || cell.product_id)
        const productId = cell.id || cell.product_id
        console.log('[UnifiedContextBar] Setting cellId to:', productId)
        setCellId(productId)

        // Use minimal cell data from allCells (already loaded) - NO API CALL NEEDED!
        // This avoids the duplicate request issue where CellPage loads full data
        // and UnifiedContextBar was making a separate request for general/chemical
        setCellData({
          manufacturer: cell.manufacturer,
          name: cell.name,
          year: cell.year,
          chemistry: { technology: cell.technology },
          chemical: { main_chemistry: cell.main_chemistry || cell.cathode_material }
        })
        console.log('[UnifiedContextBar] Using explorer data for:', productId, '(no separate API call)')

        // Load cell image using signed URL for better performance
        getSignedPictureUrl(productId, 'website_picture_cell1.png')
          .then((signedUrl) => {
            console.log('[UnifiedContextBar] Loaded cell image for:', productId)
            setImageUrl(signedUrl)
          })
          .catch((err) => {
            console.warn('[UnifiedContextBar] Failed to load cell image:', err)
            setImageUrl(null)
          })
      }

      findCell()
    }).catch((err) => {
      console.error('[UnifiedContextBar] Failed to import allCells:', err)
      setCellData(null)
      setImageUrl(null)
      setCellId(null)
    })
  }, [cellSlug])

  // Country name to ISO Alpha-2 code mapping
  const countryToISO = {
    'Japan': 'JP',
    'China': 'CN',
    'South Korea': 'KR',
    'Korea': 'KR',
    'USA': 'US',
    'United States': 'US',
    'Germany': 'DE',
    'France': 'FR',
    'UK': 'GB',
    'United Kingdom': 'GB',
    'Sweden': 'SE',
    'Netherlands': 'NL',
    'Poland': 'PL',
    'Hungary': 'HU',
    'Taiwan': 'TW',
    'Switzerland': 'CH',
    'Austria': 'AT',
    'Italy': 'IT',
    'Spain': 'ES',
    'Belgium': 'BE',
    'Czech Republic': 'CZ',
    'Finland': 'FI',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Canada': 'CA',
    'Australia': 'AU',
    'Brazil': 'BR',
    'India': 'IN'
  }

  const countryCode = cellData?.country ? countryToISO[cellData.country] : null

  const SECTIONS = [
    { id: 'factsheet', label: 'Fact Sheet' },
    { id: 'electrical', label: 'Electrical' },
    { id: 'thermal', label: 'Thermal' },
    { id: 'mechanical', label: 'Mechanical' },
    { id: 'chemical', label: 'Chemical' },
    { id: 'quality', label: 'Quality' }
  ]

  // Update inCompare state when cellId changes
  useEffect(() => {
    if (cellId) {
      import('../../state/filterStore.js').then(({ isInCompare }) => {
        setInCompare(isInCompare(cellId))
      })
    }
  }, [cellId])

  // Handle compare button click - toggle (add or remove)
  const handleCompare = () => {
    if (cellId) {
      import('../../state/filterStore.js').then(({ toggleCompare, isInCompare }) => {
        toggleCompare(cellId)
        setInCompare(isInCompare(cellId))
        console.log('[UnifiedContextBar] Toggled cell in compare:', cellId)

        // Show compare sidebar on large screens
        if (window.innerWidth >= 1600) {
          import('../../state/globalSignals.js').then(({ showCompareSidebar }) => {
            showCompareSidebar.value = true
          })
        }
      })
    }
  }

  // Get previous view for back button
  const previousView = localStorage.getItem('previousCellsView') || 'cells-grid'
  const viewLabels = {
    'cells-grid': 'Grid',
    'cells-table': 'Table',
    'cells-graph': 'Graph'
  }
  const backLabel = `Back to ${viewLabels[previousView] || 'Cells'}`

  // Extract view type from previousView (e.g., 'cells-grid' -> 'grid')
  const viewType = previousView.replace('cells-', '')

  const handleBack = () => {
    // Navigate back with the correct view parameter
    setLocation(`/explorer?view=${viewType}`)
  }

  return (
    <div className="context-bar-single-cell">
      {/* Left: Back Button + Cell Info Card */}
      <div className="cell-header-left">
        <button
          className="back-button"
          onClick={handleBack}
          title={backLabel}
        >
          <IconArrowLeft size={20} />
        </button>

        {/* Cell Info Card with Image */}
        <div className="cell-info-card">
          {imageUrl && (
            <div className="context-bar-cell-image">
              <img src={imageUrl} alt={cellData?.name || 'Cell'} />
            </div>
          )}

          <div className="cell-info-details">
            <h2 className="cell-name">
              {cellData ? `${cellData.manufacturer} ${cellData.name}` : 'Loading...'}
            </h2>
            {cellData && (
              <div className="cell-meta">
                {cellData.year && <span className="meta-year">{cellData.year}</span>}
                {cellData.chemistry?.technology && (
                  <span className="meta-chemistry">{cellData.chemistry.technology}</span>
                )}
                {cellData.chemical?.main_chemistry && (
                  <span className="meta-cathode">{cellData.chemical.main_chemistry}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Center: Section Tabs */}
      <nav className="section-tabs" data-tour="cell-section-tabs">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            className={`section-tab ${section === s.id ? 'active' : ''}`}
            onClick={() => {
              console.log('[UnifiedContextBar] Section tab clicked:', s.id)
              setLocation(`/explorer?cell=${cellSlug}&section=${s.id}`)
            }}
          >
            {s.label}
          </button>
        ))}
      </nav>

      {/* Right: Cell Actions */}
      <div className="cell-actions">
        <button
          className={`back-button ${inCompare ? 'active' : ''}`}
          title={inCompare ? "Remove from Compare" : "Add to Compare"}
          onClick={handleCompare}
          disabled={!cellId}
        >
          {inCompare ? <IconCheck size={20} /> : <IconPlus size={20} />}
        </button>
      </div>
    </div>
  )
}

/**
 * Context Bar for Radar Mode
 * Overview: Section tabs for 3 stories
 * Section: Back button + active section tab + basis toggle + plot navigation
 */
function RadarContextBar() {
  const mode = appViewMode.value
  const [, setLocation] = useLocation()

  // Extract current section from URL
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const sectionMatch = path.match(/^\/radar\/(.+)$/)
  const currentSectionId = sectionMatch ? sectionMatch[1] : null
  const currentSection = currentSectionId ? SECTION_BY_ID[currentSectionId] : null

  const activePlotIdx = radarActivePlotIndex.value
  const plotCount = radarActivePlotCount.value

  // Build slide name tooltips for dot navigation
  const slideNames = []
  if (currentSectionId) {
    // Import slide names dynamically based on section
    const slideMap = {
      'chemistry-evolution': ['Cathode Chemistry', 'NMC Nickel Evolution', 'Anode Chemistry'],
      'performance-limits': ['Ragone Envelope', 'Temporal Trends', 'Performance Envelopes'],
      'naion-vs-lfp': ['Na-Ion vs LFP Ragone', 'Evolution & KPI Profile'],
    }
    const names = slideMap[currentSectionId] || []
    for (let i = 0; i < plotCount; i++) {
      slideNames.push({ name: names[i] || `Slide ${i + 1}` })
    }
  }

  if (mode === 'radar-section' && currentSection) {
    return (
      <>
        {/* Left: Back button + Section Tabs */}
        <div className="compare-left-controls">
          <button
            className="back-button"
            onClick={() => setLocation('/radar')}
            title="Back to Radar Overview"
          >
            <IconArrowLeft size={20} />
          </button>

          <div className="section-tabs" data-tour="radar-section-tabs">
            {RADAR_SECTIONS.map(section => (
              <button
                key={section.id}
                className={`section-tab ${currentSectionId === section.id ? 'active' : ''}`}
                onClick={() => {
                  radarActivePlotIndex.value = 0
                  setLocation(section.route)
                }}
                style={{ '--tab-accent': section.accentColor }}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Dot Navigation + Basis Toggle */}
        <div className="context-controls">
          {/* Slide Arrow + Dot Navigation */}
          {plotCount > 1 && (
            <div className="context-control-group slide-nav" data-tour="radar-slide-nav">
              <button
                className="slide-nav__arrow"
                onClick={() => { if (activePlotIdx > 0) radarActivePlotIndex.value = activePlotIdx - 1 }}
                disabled={activePlotIdx === 0}
                title="Previous slide"
                aria-label="Previous slide"
              >
                <IconArrowLeft size={14} />
              </button>
              <div className="slide-dots">
                {slideNames.map((slide, i) => (
                  <button
                    key={i}
                    className={`slide-dots__dot ${i === activePlotIdx ? 'slide-dots__dot--active' : ''}`}
                    style={{ '--dot-accent': currentSection.accentColor }}
                    onClick={() => { radarActivePlotIndex.value = i }}
                    title={slide.name}
                    aria-label={`Go to slide ${i + 1}: ${slide.name}`}
                    aria-current={i === activePlotIdx ? 'step' : undefined}
                  />
                ))}
              </div>
              <button
                className="slide-nav__arrow"
                onClick={() => { if (activePlotIdx < plotCount - 1) radarActivePlotIndex.value = activePlotIdx + 1 }}
                disabled={activePlotIdx >= plotCount - 1}
                title="Next slide"
                aria-label="Next slide"
                style={{ transform: 'rotate(180deg)' }}
              >
                <IconArrowLeft size={14} />
              </button>
            </div>
          )}

          {/* PPTX Export Dropdown — only for admin + customer_success */}
          {(() => {
            const role = getAdminRole()
            const canExportPptx = role === 'admin' || role === 'customer_success'
            if (!canExportPptx) return null
            const closeMenu = () => document.querySelector('.radar-export-menu--open')?.classList.remove('radar-export-menu--open')
            return (
              <div className="context-control-group radar-export-dropdown">
                <button
                  className="radar-export-trigger"
                  onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('radar-export-menu--open')}
                  title="Export to PowerPoint"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Export
                </button>
                <div className="radar-export-menu">
                  <button className="radar-export-menu__item" onClick={() => { radarExportAction.value = 'pptx-slide'; closeMenu() }}>
                    PPTX — Current Slide
                  </button>
                  <div className="radar-export-menu__divider" />
                  <button className="radar-export-menu__item radar-export-menu__item--accent" onClick={() => { radarExportAction.value = 'pptx-section'; closeMenu() }}>
                    PPTX — Full {currentSection.title}
                  </button>
                </div>
              </div>
            )
          })()}

          {/* Basis Toggle (grav/vol) */}
          <div className="context-control-group">
            <div className="view-toggle-mini" data-tour="radar-basis-toggle">
              <button
                className={`view-toggle-btn ${basis.value === 'grav' ? 'active' : ''}`}
                onClick={() => basis.value = 'grav'}
                title="Gravimetric"
              >
                Wh/kg
              </button>
              <button
                className={`view-toggle-btn ${basis.value === 'vol' ? 'active' : ''}`}
                onClick={() => basis.value = 'vol'}
                title="Volumetric"
              >
                Wh/L
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Overview mode: Just section tabs
  return (
    <>
      <div className="compare-left-controls">
        <div className="section-tabs" data-tour="radar-section-tabs">
          {RADAR_SECTIONS.map(section => (
            <button
              key={section.id}
              className="section-tab"
              onClick={() => setLocation(section.route)}
              style={{ '--tab-accent': section.accentColor }}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>
      <div className="context-controls" />
    </>
  )
}

/**
 * Context Bar for Benchmarking Mode
 */
function BenchmarkingContextBar() {
  const mode = appViewMode.value
  const [, setLocation] = useLocation()
  const [showAdvanced, setShowAdvanced] = useState(false)

  const handlePlotChange = (view) => {
    setLocation(`/cell-selection?view=${view}`)
  }

  // KPI Groups for organized dropdowns
  const KPI_OPTIONS = {
    'Energy': [
      { value: 'energy_abs', label: 'Energy (Wh)' },
      { value: 'energy_grav', label: 'Energy (Wh/kg)' },
      { value: 'energy_vol', label: 'Energy (Wh/L)' }
    ],
    'Power': [
      { value: 'power_abs', label: 'Power (W)' },
      { value: 'power_grav', label: 'Power (W/kg)' },
      { value: 'power_vol', label: 'Power (W/L)' }
    ],
    'Capacity': [
      { value: 'capacity_abs', label: 'Capacity (Ah)' },
      { value: 'capacity_grav', label: 'Capacity (Ah/kg)' },
      { value: 'capacity_vol', label: 'Capacity (Ah/L)' }
    ],
    'Current': [
      { value: 'current_abs', label: 'Current (A)' },
      { value: 'current_grav', label: 'Current (A/kg)' },
      { value: 'current_vol', label: 'Current (A/L)' }
    ],
    'Physical': [
      { value: 'mass', label: 'Mass (g)' },
      { value: 'volume', label: 'Volume (cm³)' },
      { value: 'voltage', label: 'Voltage (V)' },
      { value: 'resistance', label: 'Resistance (mΩ)' }
    ],
    'Quality': [
      { value: 'batch_var', label: 'Cell-to-Cell Variation (%)' },
      { value: 'aging', label: 'Post-Formation Loss (%)' },
      { value: 'efficiency', label: 'DC Efficiency (%)' }
    ],
    'Other': [
      { value: 'c_rate', label: 'C-Rate' },
      { value: 'year', label: 'Year' }
    ]
  }

  const COLOR_OPTIONS = [
    { value: 'chemistry', label: 'Chemistry' },
    { value: 'technology', label: 'Technology' },
    { value: 'format', label: 'Format' },
    { value: 'manufacturer', label: 'Manufacturer' },
    { value: 'year', label: 'Year' }
  ]

  const SIZE_OPTIONS = [
    { value: 'none', label: 'None (10px)' },
    { value: 'batch-variation', label: 'Cell-to-Cell Variation' },
    { value: 'aging', label: 'Post-Formation Loss' },
    { value: 'capacity', label: 'Capacity' },
    { value: 'mass', label: 'Mass' },
    { value: 'year', label: 'Year' }
  ]

  const stepNames = {
    1: 'Set Fundamentals',
    2: 'Define Performance',
    3: 'Pick Cells'
  }

  return (
    <>
      {/* Left: Workflow Steps Indicator - All steps show names, only active is clickable */}
      <div className="plot-type-switcher">
        <div className="workflow-steps-indicator" data-tour="benchmarking-steps-indicator">
          <div
            className="step-dot-mini"
            data-active={currentStep.value === 1}
            data-disabled={currentStep.value !== 1}
          >
            <span>1</span>
            <span className="step-name">{stepNames[1]}</span>
          </div>
          <div
            className="step-dot-mini"
            data-active={currentStep.value === 2}
            data-disabled={currentStep.value !== 2}
          >
            <span>2</span>
            <span className="step-name">{stepNames[2]}</span>
          </div>
          <div
            className="step-dot-mini"
            data-active={currentStep.value === 3}
            data-disabled={currentStep.value !== 3}
          >
            <span>3</span>
            <span className="step-name">{stepNames[3]}</span>
          </div>
        </div>
      </div>

      {/* Right: Plot Controls - Removed for benchmarking */}
      {false && mode === 'benchmarking-score' && (
        <div className="context-controls">
          <div className="context-control-group">
            {/* Color By */}
            <div className="context-control">
              <label className="context-control-label">Color</label>
              <select
                className="context-control-select"
                value={scorePlotColorBy.value}
                onChange={(e) => scorePlotColorBy.value = e.target.value}
              >
                <option value="chemistry">Chemistry</option>
                <option value="country">Country</option>
                <option value="format">Format</option>
                <option value="technology">Technology</option>
                <option value="year">Year</option>
              </select>
            </div>

            {/* Size By */}
            <div className="context-control">
              <label className="context-control-label">Size</label>
              <select
                className="context-control-select"
                value={scorePlotSizeBy.value}
                onChange={(e) => scorePlotSizeBy.value = e.target.value}
              >
                <option value="none">None</option>
                <option value="batch-variation">Cell-to-Cell Variation</option>
                <option value="aging">Post-Formation Loss</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {false && mode === 'benchmarking-my' && (
        <div className="context-controls">
          <div className="context-control-group">
            {/* Color By */}
            <div className="context-control">
              <label className="context-control-label">Color</label>
              <select
                className="context-control-select"
                value={myPlotColorBy.value}
                onChange={(e) => myPlotColorBy.value = e.target.value}
              >
                <option value="chemistry">Chemistry</option>
                <option value="country">Country</option>
                <option value="format">Format</option>
                <option value="technology">Technology</option>
                <option value="year">Year</option>
              </select>
            </div>

            {/* Size By */}
            <div className="context-control">
              <label className="context-control-label">Size</label>
              <select
                className="context-control-select"
                value={myPlotSizeBy.value}
                onChange={(e) => myPlotSizeBy.value = e.target.value}
              >
                <option value="none">None</option>
                <option value="batch-variation">Cell-to-Cell Variation</option>
                <option value="aging">Post-Formation Loss</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
